/**
 * How a period is named to a human, in one place.
 *
 * The wire says `month: "2026-08"` and `from/to: "2026-08-01"`, and three screens plus
 * the period picker each used to turn that into words on their own — which is how the
 * card ended up titled «Август 2026» while the summary under it said «2026-08 — » and
 * the exported file was named after a third variant. One helper, one spelling.
 *
 * There is no `Intl.DateTimeFormat` here on purpose: Hermes ships without ICU, so month
 * names come from i18n like every other word in the app.
 */
import {useTranslation} from 'react-i18next';

import {
  clampFuture,
  periodBounds,
  type Period,
} from '@/entities/attendance/period';

/** What every report answers with about the days it covers. */
export type PeriodFields = {month?: string | null; from: string; to: string};

/**
 * A `Period` in the shape a report answers with.
 *
 * The toolbar names the period on every render, including the ones where the request
 * is still in flight — and a scope pill that goes blank while loading is worse than one
 * that is a beat behind. So the label falls back to the period that was ASKED for.
 *
 * ⚠️ It describes what will be ASKED FOR, not what was picked, and those differ: the
 * request is cut off at today (`clampFuture`), so «этот месяц» is really «месяц по
 * сегодня» and the server answers it with `month: null` and two dates. Without the same
 * cut here the pill would read «Август 2026» while the request was in flight and flip to
 * «01.08.2026 — 26.08.2026» the moment it landed — one visible jump, every load, saying
 * two different things about one period.
 */
export function describePeriod(period: Period, now: Date = new Date()): PeriodFields {
  const asked = clampFuture(period, now);
  const window = periodBounds(asked);
  return asked.kind === 'month' ? {month: asked.month, ...window} : window;
}

/** "2026-08-01" → "01.08.2026" — the way a date is written everywhere else in the UI. */
export function humanDate(date: string): string {
  return date.split('-').reverse().join('.');
}

/**
 * Два конца периода одной строкой: «01.08 — 26.08.2026».
 *
 * Год печатается ОДИН раз, если он у обоих концов один и тот же. Дважды повторённый
 * «2026» — это пять знаков, которые ничего не сообщают, и они не бесплатны: строка
 * периода стоит в пилюле тулбара и в подзаголовке карточки человека, то есть ровно там,
 * где на телефоне идёт борьба за каждый десяток точек. Разные года печатаются оба —
 * «14.12.2025 — 26.08.2026», — потому что там год и есть главное различие.
 */
export function humanRange(from: string, to: string): string {
  // Один день — одна дата. «27.08 — 27.08.2026» это не отрезок, это дата, написанная
  // дважды: журнал за сегодня и отчёт за один день подписываются именно так, и на
  // телефоне эта строка стоит в подвале панели, где лишние восемь знаков заметны.
  if (from === to) {
    return humanDate(to);
  }
  const sameYear = from.slice(0, 4) === to.slice(0, 4);
  const start = sameYear ? humanDate(from).slice(0, 5) : humanDate(from);
  return `${start} — ${humanDate(to)}`;
}

export function usePeriodLabel() {
  const {t} = useTranslation();

  /** "2026-08" → «Август 2026». */
  const month = (value: string): string => {
    const [year, mm] = value.split('-');
    return `${t(`attendance.month.${Number(mm)}`)} ${year}`;
  };

  /**
   * A whole calendar month is named; anything else is stated as its two ends. The
   * server decides which by whether it filled `month`, so the label follows the report
   * rather than re-deriving the answer and risking a different one.
   */
  const label = (fields: PeriodFields | null | undefined): string => {
    if (!fields) {
      return '';
    }
    return fields.month ? month(fields.month) : humanRange(fields.from, fields.to);
  };

  return {label, month, humanDate, humanRange};
}
